/**
 * Shared modern outlined field styles (black/slate light, slate dark).
 * Used by proposal forms and AI prompt template editors.
 */

export const getModernFieldSurface = (isDarkMode) => ({
  fieldBg: isDarkMode ? 'rgba(15, 23, 42, 0.72)' : 'rgba(255, 255, 255, 0.96)',
  fieldBorder: isDarkMode ? 'rgba(148, 163, 184, 0.28)' : 'rgba(15, 23, 42, 0.14)',
  accent: isDarkMode ? '#e2e8f0' : '#0f172a',
  muted: isDarkMode ? 'rgba(203, 213, 225, 0.72)' : 'rgba(71, 85, 105, 0.9)',
  codeBg: isDarkMode ? 'rgba(2, 6, 23, 0.72)' : 'rgba(15, 23, 42, 0.035)',
  panelBorder: isDarkMode ? 'rgba(148, 163, 184, 0.28)' : 'rgba(15, 23, 42, 0.12)',
  panelBg: isDarkMode
    ? 'linear-gradient(145deg, rgba(30, 41, 59, 0.55) 0%, rgba(15, 23, 42, 0.4) 100%)'
    : 'linear-gradient(145deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 250, 252, 0.95) 100%)',
});

/**
 * Outlined TextField / Autocomplete input look.
 * @param {boolean} isDarkMode
 * @param {object} [extra] - merged into the root sx object
 */
export const getModernTextFieldSx = (isDarkMode, extra = {}) => {
  const surface = getModernFieldSurface(isDarkMode);
  return {
    '& .MuiInputLabel-root': {
      fontWeight: 600,
      color: surface.muted,
      '&.Mui-focused': { color: surface.accent },
    },
    '& .MuiOutlinedInput-root': {
      borderRadius: '12px',
      bgcolor: surface.fieldBg,
      backdropFilter: 'blur(10px)',
      boxShadow: isDarkMode
        ? 'inset 0 1px 0 rgba(255,255,255,0.04)'
        : 'inset 0 1px 0 rgba(255,255,255,0.9), 0 1px 2px rgba(15,23,42,0.04)',
      transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
      '& fieldset': {
        borderColor: surface.fieldBorder,
      },
      '&:hover fieldset': {
        borderColor: surface.accent,
      },
      '&.Mui-focused fieldset': {
        borderColor: surface.accent,
        borderWidth: 1.5,
      },
      '&.Mui-error fieldset': {
        borderColor: 'error.main',
      },
    },
    '& .MuiFormHelperText-root': {
      fontWeight: 500,
    },
    ...extra,
  };
};

/** Grid columns for billable item rows (number + fields + amount + delete). */
export const BILLABLE_ITEMS_GRID_COLUMNS = {
  xs: '1fr',
  sm: '44px repeat(2, minmax(0, 1fr))',
  md: '40px minmax(0, 2fr) minmax(0, 7fr) minmax(0, 1.6fr) minmax(0, 1.8fr) max-content 32px',
};

export const getBillableItemsPanelSx = (isDarkMode) => {
  const surface = getModernFieldSurface(isDarkMode);
  return {
    mb: 2,
    p: 2,
    border: '1px solid',
    borderColor: surface.panelBorder,
    borderRadius: '16px',
    backgroundImage: surface.panelBg,
    boxShadow: isDarkMode
      ? '0 0 0 1px rgba(255,255,255,0.03), 0 12px 28px -22px rgba(0,0,0,0.55)'
      : '0 10px 28px -20px rgba(15, 23, 42, 0.18)',
  };
};

export const getBillableTotalRowSx = (isDarkMode) => {
  const surface = getModernFieldSurface(isDarkMode);
  return {
    width: '100%',
    display: 'grid',
    gap: 1.5,
    alignItems: 'center',
    mt: 1,
    pt: 1.5,
    borderTop: '1px solid',
    borderColor: surface.panelBorder,
    gridTemplateColumns: BILLABLE_ITEMS_GRID_COLUMNS,
  };
};

/**
 * Shared AI action button look (Write with AI / Generate line items / etc.).
 * Black/slate — no purple — for light and dark.
 */
export const getAiActionButtonSx = (isDarkMode, isBusy = false) => ({
  position: 'relative',
  overflow: 'hidden',
  isolation: 'isolate',
  textTransform: 'none',
  fontSize: '0.8125rem',
  fontWeight: 700,
  letterSpacing: '0.02em',
  borderRadius: '10px',
  px: 1.75,
  py: 0.85,
  minWidth: 0,
  color: isDarkMode ? '#0f172a' : '#ffffff',
  border: '1px solid',
  borderColor: isDarkMode ? 'rgba(226, 232, 240, 0.35)' : 'rgba(15, 23, 42, 0.2)',
  backgroundColor: 'transparent',
  backgroundImage: isDarkMode
    ? 'linear-gradient(135deg, #e2e8f0 0%, #f8fafc 55%, #cbd5e1 100%)'
    : 'linear-gradient(135deg, #0f172a 0%, #1e293b 48%, #000000 100%)',
  boxShadow: isDarkMode
    ? '0 0 0 1px rgba(255,255,255,0.06), 0 10px 28px -12px rgba(0, 0, 0, 0.55)'
    : '0 8px 22px -8px rgba(15, 23, 42, 0.45)',
  '@keyframes aiShine': {
    '0%': { transform: 'translateX(-130%) skewX(-18deg)' },
    '100%': { transform: 'translateX(230%) skewX(-18deg)' },
  },
  '@keyframes aiSparkle': {
    '0%, 100%': { transform: 'scale(1) rotate(0deg)', filter: 'drop-shadow(0 0 0 transparent)' },
    '50%': { transform: 'scale(1.18) rotate(14deg)', filter: 'drop-shadow(0 0 5px currentColor)' },
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '42%',
    height: '100%',
    background: isDarkMode
      ? 'linear-gradient(105deg, transparent 20%, rgba(15, 23, 42, 0.12) 50%, transparent 80%)'
      : 'linear-gradient(105deg, transparent 20%, rgba(255, 255, 255, 0.28) 50%, transparent 80%)',
    animation: isBusy ? 'aiShine 1.15s ease-in-out infinite' : 'aiShine 2.8s ease-in-out infinite',
    pointerEvents: 'none',
    zIndex: 0,
  },
  '& > *': {
    position: 'relative',
    zIndex: 1,
  },
  '& .MuiButton-startIcon, & .MuiButton-icon': {
    animation: isBusy ? 'none' : 'aiSparkle 2.2s ease-in-out infinite',
  },
  '&:hover': {
    backgroundColor: 'transparent',
    backgroundImage: isDarkMode
      ? 'linear-gradient(135deg, #f8fafc 0%, #ffffff 55%, #e2e8f0 100%)'
      : 'linear-gradient(135deg, #000000 0%, #0f172a 48%, #1e293b 100%)',
    boxShadow: isDarkMode
      ? '0 0 0 1px rgba(255,255,255,0.1), 0 12px 32px -10px rgba(0, 0, 0, 0.65)'
      : '0 10px 26px -6px rgba(15, 23, 42, 0.55)',
  },
  '&.Mui-disabled': {
    color: isDarkMode ? 'rgba(15, 23, 42, 0.45)' : 'rgba(255, 255, 255, 0.72)',
    borderColor: isDarkMode ? 'rgba(51, 65, 85, 0.8)' : 'rgba(148, 163, 184, 0.5)',
    backgroundColor: 'transparent',
    backgroundImage: isDarkMode
      ? 'linear-gradient(135deg, #64748b 0%, #94a3b8 100%)'
      : 'linear-gradient(135deg, #94a3b8 0%, #64748b 100%)',
    boxShadow: 'none',
    '&::before': {
      animation: 'none',
      opacity: 0,
    },
    '& .MuiButton-startIcon, & .MuiButton-icon': {
      animation: 'none',
    },
  },
  '&.Mui-disabled:hover': {
    backgroundColor: 'transparent',
    backgroundImage: isDarkMode
      ? 'linear-gradient(135deg, #64748b 0%, #94a3b8 100%)'
      : 'linear-gradient(135deg, #94a3b8 0%, #64748b 100%)',
  },
  '@media (prefers-reduced-motion: reduce)': {
    '&::before': { animation: 'none' },
    '& .MuiButton-startIcon, & .MuiButton-icon': { animation: 'none' },
  },
});
